import {
  AGENT_VAULT_CORE_ABI,
  AGENT_VAULT_CORE_BYTECODE,
} from "../contracts/vault/agent-vault-core";
import {
  AGENT_VAULT_FACTORY_ABI,
  AGENT_VAULT_FACTORY_BYTECODE,
} from "../contracts/vault/agent-vault-factory";
import {
  FEE_MODULE_ABI,
  FEE_MODULE_BYTECODE,
} from "../contracts/vault/fee-module";
import {
  IDENTITY_REGISTRY_ADAPTER_ABI,
  IDENTITY_REGISTRY_ADAPTER_BYTECODE,
} from "../contracts/vault/identity-registry-adapter";

import { deployContract } from "./deploy-utils";

import type { DeployContext } from "./deploy-utils";
import type {
  DeploymentPhaseResult,
  ERC8004Addresses,
  FoundationAddresses,
  VaultAddresses,
} from "./types";
import type { Hex } from "viem";

/**
 * Phase 8: Deploy Vault Core contracts.
 *
 * Deploys the vault infrastructure stack in dependency order:
 *   1. IdentityRegistryAdapter — bridges ERC-8004 identity + reputation
 *   2. FeeModule — management + performance fee accounting
 *   3. AgentVaultCore — ERC-4626 implementation (clone target)
 *   4. AgentVaultFactory — CREATE2 vault deployment + registry
 *
 * @param ctx        - Deploy context (clients, deployer, RPC request)
 * @param foundation - Phase 1 addresses (not used directly but available)
 * @param erc8004    - Phase 7 addresses (identity + reputation registries)
 */
export async function deployVaultCore(
  ctx: DeployContext,
  foundation: FoundationAddresses,
  erc8004: ERC8004Addresses,
): Promise<DeploymentPhaseResult<VaultAddresses>> {
  // Suppress unused param lint — foundation is accepted for API consistency
  void foundation;

  const start = Date.now();
  let totalGas = 0n;
  const txHashes: `0x${string}`[] = [];

  // 1. Deploy IdentityRegistryAdapter(identityRegistry, reputationRegistry, deployer)
  const adapterResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: IDENTITY_REGISTRY_ADAPTER_ABI,
      bytecode: IDENTITY_REGISTRY_ADAPTER_BYTECODE as Hex,
    },
    [erc8004.identityRegistry, erc8004.reputationRegistry, ctx.deployer],
  );
  totalGas += adapterResult.gasUsed;
  txHashes.push(adapterResult.txHash);

  // 2. Deploy FeeModule(owner)
  const feeModuleResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: FEE_MODULE_ABI,
      bytecode: FEE_MODULE_BYTECODE as Hex,
    },
    [ctx.deployer],
  );
  totalGas += feeModuleResult.gasUsed;
  txHashes.push(feeModuleResult.txHash);

  // 3. Deploy AgentVaultCore — implementation contract (no constructor args)
  const coreResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: AGENT_VAULT_CORE_ABI,
      bytecode: AGENT_VAULT_CORE_BYTECODE as Hex,
    },
  );
  totalGas += coreResult.gasUsed;
  txHashes.push(coreResult.txHash);

  // 4. Deploy AgentVaultFactory(implementation, adapter, feeModule, deployer)
  const factoryResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: AGENT_VAULT_FACTORY_ABI,
      bytecode: AGENT_VAULT_FACTORY_BYTECODE as Hex,
    },
    [
      coreResult.address,
      adapterResult.address,
      feeModuleResult.address,
      ctx.deployer,
    ],
  );
  totalGas += factoryResult.gasUsed;
  txHashes.push(factoryResult.txHash);

  // 5. Authorize factory to initialize vault fees in FeeModule
  const setFactoryHash = await ctx.walletClient.writeContract({
    address: feeModuleResult.address,
    abi: FEE_MODULE_ABI,
    functionName: "setAuthorizedFactory",
    args: [factoryResult.address],
    account: ctx.deployer,
    chain: ctx.walletClient.chain,
  });
  const setFactoryReceipt = await ctx.publicClient.waitForTransactionReceipt({
    hash: setFactoryHash,
  });
  totalGas += setFactoryReceipt.gasUsed;
  txHashes.push(setFactoryHash);

  return {
    phase: 8,
    name: "Vault Core",
    addresses: {
      identityAdapter: adapterResult.address,
      feeModule: feeModuleResult.address,
      vaultImplementation: coreResult.address,
      vaultFactory: factoryResult.address,
    },
    gasUsed: totalGas,
    durationMs: Date.now() - start,
    txHashes,
  };
}
